import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/capabilities", label: "Capabilities" },
  { to: "/use-cases", label: "Use Cases" },
  { to: "/contact", label: "Contact" },
];

export const Nav = () => {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-[80] transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-[#121212]/60 backdrop-blur-2xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-2 group">
          <span className="font-display text-2xl tracking-tight text-bone group-hover:text-vermilion transition-colors duration-300">
            MINDENGINE
          </span>
          <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-ash hidden sm:inline">
            Technologies
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${slug(l.label)}`}
              className={`font-mono-tech text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:text-bone ${
                pathname === l.to ? "text-vermilion" : "text-ash"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-demo"
            className="group flex items-center gap-2 bg-vermilion text-white font-mono-tech text-xs uppercase tracking-[0.2em] px-5 py-3 transition-colors duration-300 hover:bg-vermilion-dark active:scale-[0.98]"
          >
            Request Demo
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden text-bone p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-[#050505]/95 backdrop-blur-2xl border-b border-white/10"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${slug(l.label)}`}
                  className={`font-display text-3xl ${pathname === l.to ? "text-vermilion" : "text-bone"}`}
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/contact"
                data-testid="nav-mobile-cta-demo"
                className="mt-2 flex items-center justify-center gap-2 bg-vermilion text-white font-mono-tech text-xs uppercase tracking-[0.2em] px-5 py-4"
              >
                Request Demo <ArrowUpRight size={14} />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
